<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import { acceptLegal, legalState, legalUpdated, loadLegal, needsConsent } from '../legal';
import { locale, t } from '../i18n';
import Icon from './Icon.vue';

/**
 * Consent with the privacy policy and the terms of use: on first start, and again after a
 * significant change. The documents open on their own address in a new tab. Nothing else in the app
 * can be used until they are accepted (the window does not close by Esc or a click outside).
 */
const button = ref<HTMLButtonElement | null>(null);
onMounted(() => void loadLegal());
watch(needsConsent, async (v) => {
  if (!v) return;
  await nextTick();
  button.value?.focus();
});
</script>

<template>
  <div v-if="needsConsent" class="consent-backdrop">
    <div class="consent float card" role="dialog" aria-modal="true" aria-labelledby="consent-title" data-dialog="consent">
      <div class="mark"><Icon name="info" :size="20" /></div>
      <h2 id="consent-title">{{ legalUpdated ? t('legal.updatedTitle') : t('legal.title') }}</h2>
      <p class="lead">{{ legalUpdated ? t('legal.updatedText') : t('legal.text') }}</p>
      <ul class="docs">
        <li v-for="d in legalState.docs" :key="d.kind">
          <a :href="d.url" target="_blank" rel="noopener noreferrer" :data-legal="d.kind">
            <Icon name="external" :size="14" />{{ locale === 'ru' ? d.title.ru : d.title.en }}
          </a>
        </li>
      </ul>
      <p class="note">{{ t('legal.note') }}</p>
      <button ref="button" class="pill primary tall accept" data-act="accept-legal" @click="acceptLegal">{{ t('legal.accept') }}</button>
    </div>
  </div>
</template>

<style scoped>
.consent-backdrop {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: grid;
  place-items: center;
  padding: 16px;
  background: color-mix(in srgb, var(--backdrop) 80%, transparent);
}
.consent {
  position: relative;
  width: min(460px, 100%);
  padding: 26px 26px 22px;
  border-radius: 22px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: center;
  align-items: center;
}
.mark {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: var(--accent-soft);
  color: var(--accent);
}
h2 {
  margin: 0;
  font-size: 19px;
}
.lead {
  margin: 0;
  color: var(--text-2);
  line-height: 1.5;
}
.docs {
  list-style: none;
  margin: 4px 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}
.docs a {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 40px;
  border-radius: 999px;
  border: 1px solid var(--border);
  color: var(--text);
  text-decoration: none;
  font-weight: 500;
}
.docs a:hover {
  border-color: var(--accent);
  color: var(--accent);
}
.note {
  margin: 0;
  font-size: 12px;
  color: var(--muted);
}
.accept {
  margin-top: 4px;
  min-width: 180px;
  justify-content: center;
}
</style>
